/**
 * MasterBrainPanel — Cerebro Supremo de OkBot
 * Gestión centralizada 100% de todas las plataformas conectadas:
 * usuarios, configuraciones, reconciliación, bóveda, historial.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Brain, RefreshCw, Users, Settings2, GitMerge, Database,
  History, Download, Upload, Search, Edit3, Trash2, ChevronRight,
  ChevronDown, ChevronUp, Check, X, AlertTriangle, AlertCircle,
  CheckCircle2, Globe, Key, Zap, ArrowRight, ArrowUpDown,
  Copy, ExternalLink, Filter, BarChart3, Link2, Shield,
  UserPlus, Send, Eye, Save, RotateCcw, Layers, Activity,
  TrendingUp, Package
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax  = (path, opts = {}) => axios({ url: `${API}${path}`, ...opts });

// ── helpers ──────────────────────────────────────────────────────────────────
const fmt = (iso) => iso ? iso.slice(0, 16).replace('T', ' ') : '—';
const pctColor = (n) => n >= 80 ? 'text-green-400' : n >= 50 ? 'text-yellow-400' : 'text-red-400';
const SEV = {
  high:   { bg: 'bg-orange-500/10 border-orange-500/30', icon: AlertCircle,  text: 'text-orange-400', label: 'Alto' },
  medium: { bg: 'bg-yellow-500/10 border-yellow-500/30', icon: AlertTriangle, text: 'text-yellow-400', label: 'Medio' },
  low:    { bg: 'bg-blue-500/10 border-blue-500/30',     icon: AlertCircle,  text: 'text-blue-400',   label: 'Bajo' },
};
const DISC_LABELS = {
  missing_platform:       'Ausente en plataformas',
  conflicting_email:      'Email contradictorio',
  no_local_okbot_account: 'Sin cuenta OkBot',
  local_only_user:        'Solo en OkBot',
};
const OP_LABELS = {
  sync_all:          'Sync Total',
  edit_user:         'Edición Usuario',
  push_config:       'Push Config',
  reconcile_fix_all: 'Arreglo Masivo',
};

function StatCard({ icon: Icon, label, value, sub, color = 'text-white' }) {
  return (
    <div className="glass-panel p-4 flex items-center gap-3">
      <div className={`p-2.5 rounded-xl bg-white/5 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className={`text-xl font-bold ${color}`}>{value ?? '—'}</p>
        <p className="text-gray-500 text-xs">{label}</p>
        {sub && <p className="text-gray-600 text-[10px]">{sub}</p>}
      </div>
    </div>
  );
}

function PlatformBadge({ code }) {
  return (
    <span className="text-[10px] font-mono bg-white/5 border border-white/10 px-1.5 py-0.5 rounded text-gray-300">
      {code}
    </span>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: Central de Control
// ══════════════════════════════════════════════════════════════════════════════
function TabControl({ onSyncAll, syncing, stats, loadStats }) {
  return (
    <div className="space-y-4">
      {/* Hero */}
      <div className="glass-panel p-5 border border-red-500/20 bg-gradient-to-r from-red-500/5 to-transparent">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-2xl bg-red-500/10 border border-red-500/20">
            <Brain className="w-8 h-8 text-red-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-white font-bold text-lg">OkBot · Cerebro Supremo</h3>
            <p className="text-gray-400 text-sm mt-1 max-w-2xl">
              OkBot absorbe, almacena y controla el 100% de la información de todas las plataformas
              vinculadas. Usuarios, configuraciones, webhooks y datos fluyen a través de OkBot como
              registro maestro único y fuente de verdad.
            </p>
            <div className="flex flex-wrap gap-3 mt-3">
              <button onClick={onSyncAll} disabled={syncing}
                className="btn-primary flex items-center gap-2 text-sm">
                {syncing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                {syncing ? 'Absorbiendo datos...' : 'Absorber Todo Ahora'}
              </button>
              <button onClick={loadStats} className="btn-secondary flex items-center gap-2 text-sm">
                <RefreshCw className="w-4 h-4" />Actualizar Stats
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      {stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard icon={Globe}      label="Plataformas activas"    value={stats.total_platforms}      color="text-blue-400" />
            <StatCard icon={Users}      label="Usuarios unificados"    value={stats.total_master_users}   color="text-green-400" />
            <StatCard icon={Link2}      label="Vinculados a OkBot"     value={`${stats.link_rate}%`}      color="text-purple-400"
              sub={`${stats.linked_to_okbot} de ${stats.total_master_users}`} />
            <StatCard icon={Settings2}  label="Configs almacenadas"   value={stats.total_configs_stored}  color="text-yellow-400" />
          </div>

          {/* Distribución por plataforma */}
          {stats.platform_distribution?.length > 0 && (
            <div className="glass-panel p-4">
              <h4 className="text-sm font-semibold text-gray-200 mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-red-400" />Usuarios por plataforma
              </h4>
              <div className="space-y-2">
                {stats.platform_distribution.map(pd => {
                  const pct = stats.total_master_users ? Math.round(pd.user_count / stats.total_master_users * 100) : 0;
                  return (
                    <div key={pd.platform_code}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <PlatformBadge code={pd.platform_code} />
                          <span className="text-gray-500 text-xs truncate max-w-xs">{pd.url}</span>
                        </div>
                        <span className="text-white text-xs font-bold">{pd.user_count}</span>
                      </div>
                      <div className="w-full bg-white/5 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full bg-red-500 transition-all"
                          style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Alertas de estado */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="glass-panel p-3">
              <p className="text-gray-500 text-xs mb-1">Última sincronización total</p>
              <p className="text-white font-mono text-sm">{fmt(stats.last_sync)}</p>
            </div>
            <div className="glass-panel p-3">
              <p className="text-gray-500 text-xs mb-1">Última reconciliación</p>
              <p className={`font-mono text-sm ${stats.last_reconcile_discrepancies > 0 ? 'text-yellow-400' : 'text-white'}`}>
                {fmt(stats.last_reconcile)}
                {stats.last_reconcile_discrepancies > 0 &&
                  <span className="text-[10px] ml-2">({stats.last_reconcile_discrepancies} discrepancias)</span>}
              </p>
            </div>
          </div>

          {/* Operaciones recientes */}
          {stats.recent_operations?.length > 0 && (
            <div className="glass-panel p-4">
              <h4 className="text-sm font-semibold text-gray-200 mb-3 flex items-center gap-2">
                <Activity className="w-4 h-4 text-red-400" />Operaciones recientes
              </h4>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {stats.recent_operations.map((op, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs py-1 border-b border-white/5">
                    <span className="text-gray-400 font-mono min-w-[120px]">{fmt(op.ran_at)}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                      op.status === 'ok' ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'
                    }`}>{op.status?.toUpperCase()}</span>
                    <span className="text-gray-300">{OP_LABELS[op.operation] || op.operation}</span>
                    {op.platform_code && <PlatformBadge code={op.platform_code} />}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: Usuarios Unificados
// ══════════════════════════════════════════════════════════════════════════════
function TabUsers({ onSyncAll }) {
  const [users, setUsers]         = useState([]);
  const [total, setTotal]         = useState(0);
  const [pages, setPages]         = useState(1);
  const [page, setPage]           = useState(1);
  const [search, setSearch]       = useState('');
  const [loading, setLoading]     = useState(false);
  const [selected, setSelected]   = useState(null);
  const [editing, setEditing]     = useState(false);
  const [editForm, setEditForm]   = useState({});
  const [saving, setSaving]       = useState(false);
  const [pushing, setPushing]     = useState(false);
  const searchTimer = useRef(null);

  const load = useCallback(async (p = 1, q = search) => {
    setLoading(true);
    try {
      const r = await ax(`/admin/connect/master/users?page=${p}&limit=30&search=${encodeURIComponent(q)}`);
      setUsers(r.data.users || []);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
      setPage(p);
    } catch { toast.error('Error cargando usuarios'); }
    finally { setLoading(false); }
  }, [search]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const handleSearch = (v) => {
    setSearch(v);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => load(1, v), 400);
  };

  const openUser = async (u) => {
    setSelected(null);
    try {
      const r = await ax(`/admin/connect/master/users/${u.id || u._id}`);
      setSelected(r.data);
      setEditForm({
        name: r.data.name || '',
        email: r.data.email || '',
        phone: r.data.phone || '',
        unified_role: r.data.unified_role || 'user',
        unified_status: r.data.unified_status || 'active',
        notes: r.data.notes || '',
      });
    } catch { toast.error('Error cargando usuario'); }
  };

  const saveEdit = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const r = await ax(`/admin/connect/master/users/${selected.id || selected._id}`, {
        method: 'PUT',
        data: { ...editForm, push_to_platforms: true }
      });
      setSelected(r.data.user);
      setEditing(false);
      toast.success(`Usuario actualizado y enviado a ${r.data.push_results?.filter(p => p.pushed).length} plataformas`);
      load(page);
    } catch { toast.error('Error guardando usuario'); }
    finally { setSaving(false); }
  };

  const pushToAll = async () => {
    if (!selected) return;
    setPushing(true);
    try {
      const r = await ax(`/admin/connect/master/users/${selected.id || selected._id}/push-to-all`, { method: 'POST' });
      const ok = r.data.results?.filter(x => x.ok).length || 0;
      toast.success(`Usuario propagado a ${ok} plataformas`);
    } catch { toast.error('Error propagando usuario'); }
    finally { setPushing(false); }
  };

  return (
    <div className="flex gap-4 h-[calc(100vh-220px)] min-h-[500px]">
      {/* List panel */}
      <div className="w-80 flex-shrink-0 flex flex-col gap-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
          <input value={search} onChange={e => handleSearch(e.target.value)}
            placeholder="Buscar por email, nombre..."
            className="input-field pl-9 text-xs w-full" />
        </div>
        <div className="flex items-center justify-between text-xs text-gray-500 px-1">
          <span>{total} usuarios unificados</span>
          <button onClick={() => load(page)} className="hover:text-white transition-colors">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-1 pr-1">
          {loading && <div className="text-center py-8 text-gray-600 text-sm">Cargando...</div>}
          {!loading && users.map(u => (
            <button key={u._id || u.id} onClick={() => openUser(u)}
              className={`w-full text-left p-3 rounded-xl border transition-all ${
                selected?._id === u._id || selected?.id === u.id
                  ? 'border-red-500/40 bg-red-500/10'
                  : 'border-white/5 bg-white/3 hover:bg-white/6 hover:border-white/10'
              }`}>
              <div className="flex items-start gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-500/30 to-purple-500/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-white">
                    {(u.name || u.email || '?')[0].toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs font-medium truncate">{u.name || '(sin nombre)'}</p>
                  <p className="text-gray-500 text-[10px] truncate">{u.email}</p>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {(u.platform_codes || []).slice(0, 3).map(pc => (
                      <PlatformBadge key={pc} code={pc.slice(-6)} />
                    ))}
                    {(u.platform_codes?.length || 0) > 3 &&
                      <span className="text-[9px] text-gray-600">+{u.platform_codes.length - 3}</span>}
                  </div>
                </div>
                <ChevronRight className="w-3 h-3 text-gray-600 flex-shrink-0 mt-1" />
              </div>
            </button>
          ))}
          {!loading && users.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-8 h-8 text-gray-700 mx-auto mb-2" />
              <p className="text-gray-600 text-sm">Sin usuarios</p>
              <p className="text-gray-700 text-xs mt-1">Ejecuta "Absorber Todo" para importar</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex gap-1">
            {Array.from({ length: Math.min(pages, 5) }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => load(p)}
                className={`flex-1 py-1 text-xs rounded-lg transition-colors ${
                  page === p ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}>{p}</button>
            ))}
          </div>
        )}
      </div>

      {/* Detail panel */}
      <div className="flex-1 glass-panel overflow-y-auto">
        {!selected && (
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <Users className="w-12 h-12 text-gray-700 mb-3" />
            <p className="text-gray-500">Selecciona un usuario para ver su perfil completo</p>
            <p className="text-gray-700 text-sm mt-2">
              Podrás ver sus datos en todas las plataformas, editarlos y propagarlos.
            </p>
          </div>
        )}
        {selected && (
          <div className="p-5 space-y-4">
            {/* Header */}
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-500/40 to-purple-500/40 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl font-bold text-white">
                  {(selected.name || selected.email || '?')[0].toUpperCase()}
                </span>
              </div>
              <div className="flex-1">
                <h3 className="text-white font-bold text-lg">{selected.name || '(sin nombre)'}</h3>
                <p className="text-gray-400 text-sm">{selected.email}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${
                    selected.unified_status === 'active'
                      ? 'text-green-400 bg-green-500/10 border-green-500/20'
                      : 'text-gray-400 bg-white/5 border-white/10'
                  }`}>{selected.unified_status}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full border text-blue-400 bg-blue-500/10 border-blue-500/20">
                    {selected.unified_role}
                  </span>
                  {selected.okbot_linked &&
                    <span className="text-xs px-2 py-0.5 rounded-full border text-purple-400 bg-purple-500/10 border-purple-500/20">
                      ✓ OkBot vinculado
                    </span>}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setEditing(!editing); }} title="Editar"
                  className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors">
                  <Edit3 className="w-4 h-4" />
                </button>
                <button onClick={pushToAll} disabled={pushing} title="Propagar a todas las plataformas"
                  className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors">
                  {pushing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Edit form */}
            {editing && (
              <div className="bg-white/3 border border-white/10 rounded-2xl p-4 space-y-3">
                <p className="text-xs font-semibold text-gray-300 mb-2">
                  Editar — los cambios se enviarán a todas las plataformas vinculadas
                </p>
                {[
                  { key: 'name', label: 'Nombre' }, { key: 'email', label: 'Email' },
                  { key: 'phone', label: 'Teléfono' },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <label className="text-[10px] text-gray-500 uppercase">{label}</label>
                    <input value={editForm[key] || ''} onChange={e => setEditForm(f => ({ ...f, [key]: e.target.value }))}
                      className="input-field text-xs w-full mt-1" />
                  </div>
                ))}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'unified_role', label: 'Rol', opts: ['user','admin','moderator','agent','viewer'] },
                    { key: 'unified_status', label: 'Estado', opts: ['active','inactive','suspended','pending'] },
                  ].map(({ key, label, opts }) => (
                    <div key={key}>
                      <label className="text-[10px] text-gray-500 uppercase">{label}</label>
                      <select value={editForm[key] || ''} onChange={e => setEditForm(f => ({ ...f, [key]: e.target.value }))}
                        className="input-field text-xs w-full mt-1">
                        {opts.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 uppercase">Notas internas</label>
                  <textarea value={editForm.notes || ''} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                    rows={2} className="input-field text-xs w-full mt-1 resize-none" />
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={saveEdit} disabled={saving}
                    className="btn-primary flex items-center gap-1.5 text-xs">
                    {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    {saving ? 'Guardando...' : 'Guardar y Propagar'}
                  </button>
                  <button onClick={() => setEditing(false)} className="btn-secondary text-xs">Cancelar</button>
                </div>
              </div>
            )}

            {/* Platform presence */}
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">
                Presencia en plataformas ({Object.keys(selected.platforms || {}).length})
              </h4>
              <div className="space-y-2">
                {Object.entries(selected.platforms || {}).map(([pcode, pdata]) => (
                  <div key={pcode} className="bg-white/3 border border-white/10 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-2">
                      <PlatformBadge code={pcode} />
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        pdata.status === 'active' ? 'text-green-400 bg-green-500/10' : 'text-gray-500 bg-white/5'
                      }`}>{pdata.status}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
                      {[
                        ['ID remoto', pdata.remote_id],
                        ['Email', pdata.email],
                        ['Username', pdata.username],
                        ['Rol', pdata.role],
                        ['Plan', pdata.plan],
                        ['Sincronizado', fmt(pdata.synced_at)],
                      ].map(([k, v]) => v ? (
                        <div key={k} className="flex gap-1">
                          <span className="text-gray-600">{k}:</span>
                          <span className="text-gray-300 truncate">{v}</span>
                        </div>
                      ) : null)}
                    </div>
                  </div>
                ))}
                {Object.keys(selected.platforms || {}).length === 0 && (
                  <p className="text-gray-600 text-xs">Sin presencia en plataformas externas</p>
                )}
              </div>
            </div>

            {/* OkBot local data */}
            {selected.okbot_local_data && (
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">Cuenta Local OkBot</h4>
                <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-3">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
                    {Object.entries(selected.okbot_local_data).filter(([, v]) => v).map(([k, v]) => (
                      <div key={k} className="flex gap-1">
                        <span className="text-gray-600">{k}:</span>
                        <span className="text-purple-300 truncate">{String(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: Configuraciones
// ══════════════════════════════════════════════════════════════════════════════
function TabConfigs() {
  const [configs, setConfigs]       = useState([]);
  const [selected, setSelected]     = useState(null);
  const [overrides, setOverrides]   = useState('{}');
  const [loading, setLoading]       = useState(false);
  const [pulling, setPulling]       = useState(null);
  const [pushing, setPushing]       = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await ax('/admin/connect/master/configs');
      setConfigs(r.data.configs || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const selectConfig = (c) => {
    setSelected(c);
    setOverrides(JSON.stringify(c.local_overrides || {}, null, 2));
  };

  const pullConfig = async (pcode) => {
    setPulling(pcode);
    try {
      const r = await ax(`/admin/connect/master/configs/${pcode}/pull`, { method: 'POST' });
      toast.success('Config extraída de la plataforma');
      load();
      if (selected?.platform_code === pcode) setSelected({ ...selected, remote_config: r.data.config });
    } catch { toast.error('Error extrayendo config'); }
    finally { setPulling(null); }
  };

  const pushConfig = async () => {
    if (!selected) return;
    let parsed;
    try { parsed = JSON.parse(overrides); }
    catch { toast.error('JSON inválido en los overrides'); return; }
    setPushing(selected.platform_code);
    try {
      await ax(`/admin/connect/master/configs/${selected.platform_code}`, {
        method: 'PUT',
        data: { overrides: parsed, push_to_platform: true }
      });
      toast.success('Configuración guardada y enviada a la plataforma');
      load();
    } catch { toast.error('Error enviando configuración'); }
    finally { setPushing(null); }
  };

  return (
    <div className="flex gap-4 h-[calc(100vh-220px)] min-h-[500px]">
      {/* List */}
      <div className="w-72 flex-shrink-0 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">{configs.length} configs almacenadas</p>
          <button onClick={load} className="p-1 hover:text-white text-gray-500 transition-colors">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto space-y-1">
          {configs.map(c => (
            <button key={c.platform_code} onClick={() => selectConfig(c)}
              className={`w-full text-left p-3 rounded-xl border transition-all ${
                selected?.platform_code === c.platform_code
                  ? 'border-red-500/40 bg-red-500/10'
                  : 'border-white/5 bg-white/3 hover:bg-white/6'
              }`}>
              <div className="flex items-center justify-between mb-1">
                <PlatformBadge code={c.platform_code} />
                <span className={`text-[9px] ${Object.keys(c.remote_config || {}).length ? 'text-green-400' : 'text-gray-600'}`}>
                  {Object.keys(c.remote_config || {}).length} keys
                </span>
              </div>
              <p className="text-gray-500 text-[10px] truncate">{c.platform_url}</p>
              <p className="text-gray-700 text-[10px]">Extraído: {fmt(c.last_pull)}</p>
            </button>
          ))}
          {!loading && configs.length === 0 && (
            <p className="text-center py-8 text-gray-600 text-sm">Sin configs. Ejecuta "Absorber Todo".</p>
          )}
        </div>
      </div>

      {/* Detail */}
      <div className="flex-1 glass-panel overflow-y-auto p-5 space-y-4">
        {!selected && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Settings2 className="w-10 h-10 text-gray-700 mb-3" />
            <p className="text-gray-500">Selecciona una plataforma para ver y editar su configuración</p>
          </div>
        )}
        {selected && (
          <>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white font-bold flex items-center gap-2">
                  <PlatformBadge code={selected.platform_code} />
                  Configuración
                </h3>
                <p className="text-gray-500 text-xs">{selected.platform_url}</p>
              </div>
              <button onClick={() => pullConfig(selected.platform_code)}
                disabled={pulling === selected.platform_code}
                className="btn-secondary flex items-center gap-2 text-xs">
                {pulling === selected.platform_code
                  ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  : <Download className="w-3.5 h-3.5" />}
                Extraer de Plataforma
              </button>
            </div>

            {/* Remote config viewer */}
            {Object.keys(selected.remote_config || {}).length > 0 && (
              <div>
                <p className="text-xs text-gray-500 mb-1.5">Config remota actual (solo lectura)</p>
                <div className="bg-black/30 rounded-xl p-3 max-h-48 overflow-y-auto border border-white/5">
                  <pre className="text-[10px] text-gray-300 font-mono whitespace-pre-wrap">
                    {JSON.stringify(selected.remote_config, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {/* Override editor */}
            <div>
              <p className="text-xs text-gray-400 mb-1.5 flex items-center gap-1.5">
                <Edit3 className="w-3 h-3" />
                Overrides de OkBot (se enviarán a la plataforma)
              </p>
              <textarea
                value={overrides}
                onChange={e => setOverrides(e.target.value)}
                rows={10}
                className="w-full bg-black/30 border border-white/10 rounded-xl p-3 text-[11px] font-mono text-gray-200 resize-none focus:outline-none focus:border-red-500/50"
                placeholder='{"key": "value"}'
              />
              <div className="flex gap-2 mt-2">
                <button onClick={pushConfig} disabled={!!pushing}
                  className="btn-primary flex items-center gap-2 text-xs">
                  {pushing ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                  Guardar y Enviar a Plataforma
                </button>
                <button onClick={() => setOverrides(JSON.stringify(selected.local_overrides || {}, null, 2))}
                  className="btn-secondary text-xs">Resetear</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: Reconciliación
// ══════════════════════════════════════════════════════════════════════════════
function TabReconcile() {
  const [running, setRunning]   = useState(false);
  const [fixing, setFixing]     = useState(false);
  const [result, setResult]     = useState(null);

  const runReconcile = async () => {
    setRunning(true);
    try {
      const r = await ax('/admin/connect/master/reconcile', { method: 'POST' });
      setResult(r.data);
      toast[r.data.discrepancies_found > 0 ? 'warning' : 'success'](
        `Reconciliación: ${r.data.discrepancies_found} discrepancias en ${r.data.users_scanned} usuarios`
      );
    } catch { toast.error('Error en reconciliación'); }
    finally { setRunning(false); }
  };

  const fixAll = async () => {
    setFixing(true);
    try {
      const r = await ax('/admin/connect/master/reconcile/fix-all', { method: 'POST' });
      toast.success(`${r.data.fixed} discrepancias corregidas automáticamente`);
      runReconcile();
    } catch { toast.error('Error aplicando correcciones'); }
    finally { setFixing(false); }
  };

  const bySeverity = (sev) => result?.discrepancies?.filter(d => d.severity === sev) || [];
  const byType = (type) => result?.discrepancies?.filter(d => d.type === type) || [];

  return (
    <div className="space-y-4">
      <div className="glass-panel p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h4 className="text-white font-semibold flex items-center gap-2">
              <GitMerge className="w-4 h-4 text-red-400" />Motor de Reconciliación
            </h4>
            <p className="text-gray-500 text-xs mt-1">
              Detecta usuarios y datos contradictorios entre OkBot y todas las plataformas.
              Propone correcciones y las aplica automáticamente.
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={runReconcile} disabled={running}
              className="btn-primary flex items-center gap-2 text-sm">
              {running ? <RefreshCw className="w-4 h-4 animate-spin" /> : <GitMerge className="w-4 h-4" />}
              {running ? 'Analizando...' : 'Analizar Discrepancias'}
            </button>
            {result?.discrepancies_found > 0 && (
              <button onClick={fixAll} disabled={fixing}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-orange-500/20 border border-orange-500/30 text-orange-400 hover:bg-orange-500/30 transition-colors text-sm">
                {fixing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                {fixing ? 'Arreglando...' : 'Arreglar Todo Auto'}
              </button>
            )}
          </div>
        </div>

        {result && (
          <div className="grid grid-cols-3 gap-3 mt-4">
            {[
              { label: 'Usuarios escaneados',   val: result.users_scanned,         color: 'text-white' },
              { label: 'Discrepancias',          val: result.discrepancies_found,   color: result.discrepancies_found > 0 ? 'text-yellow-400' : 'text-green-400' },
              { label: 'Corregidas auto',        val: result.auto_fixed || 0,       color: 'text-green-400' },
            ].map(({ label, val, color }) => (
              <div key={label} className="bg-white/5 rounded-xl p-3 text-center">
                <p className={`text-xl font-bold ${color}`}>{val}</p>
                <p className="text-gray-500 text-xs">{label}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {result && result.discrepancies?.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-300">
            Discrepancias detectadas ({result.discrepancies.length})
          </h4>

          {['missing_platform','conflicting_email','no_local_okbot_account','local_only_user'].map(type => {
            const items = byType(type);
            if (!items.length) return null;
            const cfg = items[0].severity in SEV ? SEV[items[0].severity] : SEV.medium;
            const Icon = cfg.icon;
            return (
              <div key={type} className={`glass-panel overflow-hidden`}>
                <div className={`flex items-center gap-3 p-3 border-b border-white/5 ${cfg.bg}`}>
                  <Icon className={`w-4 h-4 ${cfg.text}`} />
                  <div>
                    <span className={`font-semibold text-sm ${cfg.text}`}>{DISC_LABELS[type] || type}</span>
                    <span className="text-gray-500 text-xs ml-2">({items.length} caso{items.length > 1 ? 's' : ''})</span>
                  </div>
                </div>
                <div className="p-3 max-h-64 overflow-y-auto space-y-2">
                  {items.map((d, i) => (
                    <div key={i} className="flex items-start gap-3 text-xs py-1.5 border-b border-white/5 last:border-0">
                      <div className="flex-1">
                        <p className="text-gray-200">{d.user_email || d.user_name || '(usuario)'}</p>
                        {d.missing_platforms?.length > 0 && (
                          <p className="text-gray-500 mt-0.5">
                            Falta en: {d.missing_platforms.map(p => (
                              <PlatformBadge key={p} code={p.slice(-6)} />
                            ))}
                          </p>
                        )}
                        {d.emails_found?.length > 0 && (
                          <p className="text-gray-500 mt-0.5">Emails: {d.emails_found.join(' vs ')}</p>
                        )}
                      </div>
                      <span className="text-gray-600 bg-white/5 px-2 py-0.5 rounded text-[10px]">
                        fix: {d.fix}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {result && result.discrepancies?.length === 0 && (
        <div className="glass-panel p-10 text-center">
          <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
          <p className="text-green-400 font-semibold text-lg">Infraestructura 100% consistente</p>
          <p className="text-gray-500 text-sm mt-1">No se detectaron discrepancias entre OkBot y las plataformas.</p>
        </div>
      )}

      {!result && (
        <div className="glass-panel p-12 text-center">
          <GitMerge className="w-12 h-12 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500">Ejecuta el análisis para detectar discrepancias</p>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: Historial
// ══════════════════════════════════════════════════════════════════════════════
function TabHistory() {
  const [logs, setLogs]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await ax(`/admin/connect/master/sync-log?limit=100${filter ? `&operation=${filter}` : ''}`);
      setLogs(r.data.logs || []);
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const OPS = ['', 'sync_all', 'edit_user', 'push_config', 'reconcile_fix_all'];

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        {OPS.map(op => (
          <button key={op} onClick={() => setFilter(op)}
            className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-colors ${
              filter === op ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}>
            {op ? (OP_LABELS[op] || op) : 'Todas'}
          </button>
        ))}
        <button onClick={load} className="ml-auto p-1.5 text-gray-500 hover:text-white transition-colors">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="space-y-2">
        {logs.map((log, i) => (
          <div key={i} className="glass-panel p-3">
            <div className="flex items-start gap-3">
              <span className={`text-xs px-2 py-0.5 rounded font-semibold flex-shrink-0 ${
                log.status === 'ok' ? 'bg-green-500/10 text-green-400' :
                log.status === 'partial' ? 'bg-yellow-500/10 text-yellow-400' :
                'bg-red-500/10 text-red-400'
              }`}>{log.status?.toUpperCase() || 'OK'}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white text-xs font-medium">{OP_LABELS[log.operation] || log.operation}</span>
                  {log.platform_code && <PlatformBadge code={log.platform_code} />}
                  {log.user_id && <span className="text-gray-600 text-[10px]">uid:{log.user_id.slice(-8)}</span>}
                </div>
                {log.summary && (
                  <p className="text-gray-500 text-[10px] mt-0.5">
                    {log.summary.platforms_synced && `${log.summary.platforms_synced} plats · `}
                    {log.summary.users_created && `+${log.summary.users_created} creados · `}
                    {log.summary.users_updated && `↻${log.summary.users_updated} actualizados`}
                  </p>
                )}
                {log.errors?.length > 0 && (
                  <p className="text-red-500 text-[10px] mt-0.5">{log.errors.length} errores</p>
                )}
              </div>
              <span className="text-gray-600 text-[10px] font-mono flex-shrink-0">{fmt(log.ran_at)}</span>
            </div>
          </div>
        ))}
        {!loading && logs.length === 0 && (
          <div className="glass-panel p-10 text-center">
            <History className="w-10 h-10 text-gray-700 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">Sin operaciones registradas</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: Registro Global
// ══════════════════════════════════════════════════════════════════════════════
function TabRegistry() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await ax('/admin/connect/master/registry');
      setData(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-sm font-semibold text-gray-200">Registro Maestro Completo de OkBot</h4>
        <button onClick={load} className="btn-secondary flex items-center gap-2 text-xs">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />Actualizar
        </button>
      </div>

      {loading && <div className="text-center py-12 text-gray-500">Cargando registro...</div>}

      {data && (
        <>
          {/* Platforms */}
          <div className="glass-panel p-4">
            <h5 className="text-xs font-semibold text-gray-400 uppercase mb-3 flex items-center gap-2">
              <Globe className="w-3.5 h-3.5" />Plataformas ({data.platforms?.length || 0})
            </h5>
            <div className="space-y-2">
              {data.platforms?.map(p => (
                <div key={p.id} className="flex items-center gap-3 p-2.5 bg-white/3 rounded-xl">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${p.status === 'active' ? 'bg-green-400' : 'bg-gray-500'}`} />
                  <PlatformBadge code={p.platform_code} />
                  <span className="text-gray-400 text-xs truncate flex-1">{p.platform_url}</span>
                  <span className={`text-[10px] ${p.api_key_b64 ? 'text-green-400' : 'text-red-400'}`}>
                    {p.api_key_b64 ? '🔑 API Key' : '⚠ Sin key'}
                  </span>
                  <span className="text-gray-600 text-[10px]">{p.oauth_sso_status || 'no oauth'}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Webhooks */}
          {data.webhooks?.length > 0 && (
            <div className="glass-panel p-4">
              <h5 className="text-xs font-semibold text-gray-400 uppercase mb-3">
                Webhooks ({data.webhooks.length})
              </h5>
              <div className="space-y-1.5">
                {data.webhooks.map((wh, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs p-2 bg-white/3 rounded-lg">
                    <PlatformBadge code={wh.platform_code || '?'} />
                    <span className="text-gray-400 flex-1 truncate">{wh.event || wh.url}</span>
                    <span className={`text-[10px] ${wh.active ? 'text-green-400' : 'text-gray-600'}`}>
                      {wh.active ? 'activo' : 'inactivo'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Export */}
          <div className="glass-panel p-4">
            <h5 className="text-xs font-semibold text-gray-400 uppercase mb-3">
              Exportar Datos
            </h5>
            <div className="flex gap-2">
              <button onClick={async () => {
                const r = await ax('/admin/connect/master/export/users');
                const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = `okbot-master-users-${Date.now()}.json`;
                a.click();
                toast.success(`${r.data.total} usuarios exportados`);
              }} className="btn-secondary flex items-center gap-2 text-xs">
                <Download className="w-3.5 h-3.5" />Exportar Usuarios (JSON)
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN
// ══════════════════════════════════════════════════════════════════════════════
const TABS = [
  { id: 'control',    label: 'Central de Control',    icon: Brain },
  { id: 'users',      label: 'Usuarios Unificados',   icon: Users },
  { id: 'configs',    label: 'Configuraciones',       icon: Settings2 },
  { id: 'reconcile',  label: 'Reconciliación',        icon: GitMerge },
  { id: 'history',    label: 'Historial',             icon: History },
  { id: 'registry',   label: 'Registro Global',       icon: Database },
];

export default function MasterBrainPanel() {
  const [tab, setTab]       = useState('control');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [stats, setStats]   = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const r = await ax('/admin/connect/master/stats');
      setStats(r.data);
    } catch {}
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      toast.info('OkBot absorbiendo datos de todas las plataformas...');
      const r = await ax('/admin/connect/master/sync-all', { method: 'POST' });
      setSyncResult(r.data);
      await loadStats();
      toast.success(
        `Sync completo: ${r.data.platforms_synced} plataformas · ` +
        `${r.data.users_created} nuevos · ${r.data.users_updated} actualizados`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en sync');
    } finally { setSyncing(false); }
  };

  return (
    <div className="space-y-4">
      {/* Header + sync result banner */}
      {syncResult && (
        <div className="glass-panel p-3 border border-green-500/20 bg-green-500/5 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
          <div className="flex-1 text-xs text-gray-300">
            Última absorción: <strong className="text-white">{syncResult.platforms_synced}</strong> plataformas ·{' '}
            <strong className="text-green-400">+{syncResult.users_created}</strong> creados ·{' '}
            <strong className="text-blue-400">↻{syncResult.users_updated}</strong> actualizados ·{' '}
            <strong className="text-yellow-400">{syncResult.configs_pulled}</strong> configs extraídas
          </div>
          <button onClick={() => setSyncResult(null)} className="text-gray-600 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Tab bar */}
      <div className="flex flex-wrap gap-1.5">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
              tab === id
                ? 'bg-red-500 text-white shadow-lg shadow-red-500/20'
                : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-gray-200'
            }`}>
            <Icon className="w-3.5 h-3.5" />{label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'control'   && <TabControl onSyncAll={handleSyncAll} syncing={syncing} stats={stats} loadStats={loadStats} />}
      {tab === 'users'     && <TabUsers onSyncAll={handleSyncAll} />}
      {tab === 'configs'   && <TabConfigs />}
      {tab === 'reconcile' && <TabReconcile />}
      {tab === 'history'   && <TabHistory />}
      {tab === 'registry'  && <TabRegistry />}
    </div>
  );
}
